import React, { useState, useEffect, useCallback } from 'react';
import { Button, TextField, Box, CircularProgress, Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Typography } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { getProductosEnInventario } from '../../../api/productos';
import { getProductosDeCliente, asignarProductoACliente, quitarProductoDeCliente } from '../../../api/clienteProductos';

interface Producto {
  id: number;
  nombre: string;
  unidad: string;
}

interface ProductoAsignado {
  ClPr_Id: number;
  ClPr_Pr_Id: number;
  Pr_Nombre: string;
  Un_Nombre: string;
}

// Que productos puede pedir este cliente al crear un Preservicio -- si no tiene ninguno
// asignado, el desplegable de Preservicios le aparece vacio (sin fallback al catalogo completo).
const AssignProductos = React.memo(({ cliente, onClose }: any) => {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [selectedProducto, setSelectedProducto] = useState<Producto | null>(null);
  const [asignados, setAsignados] = useState<ProductoAsignado[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchProductos = useCallback(async () => {
    try {
      const response = await getProductosEnInventario();
      if (response.isOk && response.data) {
        const mappedProductos = response.data.map((prod: any) => ({
          id: prod.Pr_Id,
          nombre: prod.Pr_Nombre,
          unidad: prod.Un_Nombre,
        }));
        setProductos(mappedProductos);
      } else {
        setProductos([]);
      }
    } catch (error) {
      console.error('Error inesperado al cargar los productos:', error);
    }
  }, []);

  const fetchAsignados = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProductosDeCliente(cliente.Cl_Id);
      if (response.isOk && response.data) {
        setAsignados(response.data);
      } else {
        console.error('Error al cargar los productos asignados:', response.message);
      }
    } catch (error) {
      console.error('Error inesperado al cargar los productos asignados:', error);
    } finally {
      setLoading(false);
    }
  }, [cliente.Cl_Id]);

  useEffect(() => {
    fetchProductos();
    fetchAsignados();
  }, [fetchProductos, fetchAsignados]);

  const handleAgregar = async () => {
    if (!selectedProducto) {
      setError('Selecciona un producto antes de agregarlo');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await asignarProductoACliente({ ClPr_Cl_Id: cliente.Cl_Id, ClPr_Pr_Id: selectedProducto.id });
      if (response.isOk) {
        setSelectedProducto(null);
        await fetchAsignados();
      } else {
        setError(response.message || 'Error al asignar el producto');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQuitar = async (clPrId: number) => {
    setLoading(true);
    try {
      await quitarProductoDeCliente(clPrId);
      await fetchAsignados();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Typography variant='body2' sx={{ marginBottom: '0.8rem' }}>
        <strong>Cliente:</strong> {cliente.Cl_Nombre}
      </Typography>

      <Box display='flex' gap={1} alignItems='flex-start' sx={{ marginBottom: '0.8rem' }}>
        <Autocomplete
          fullWidth
          options={productos}
          getOptionLabel={(option) => option.nombre}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedProducto}
          onChange={(event, value) => setSelectedProducto(value)}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.nombre} {option.unidad}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label='Producto' variant='outlined' fullWidth />}
        />
        <Button onClick={handleAgregar} variant='outlined' disabled={loading} sx={{ whiteSpace: 'nowrap' }}>
          {loading ? <CircularProgress size={24} /> : 'Agregar'}
        </Button>
      </Box>

      {error && (
        <Typography variant='caption' color='error' sx={{ marginBottom: '0.8rem', display: 'block' }}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ marginTop: '1rem' }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Producto</TableCell>
              <TableCell>Unidad</TableCell>
              <TableCell align='center'>Quitar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {asignados.map((producto) => (
              <TableRow key={producto.ClPr_Id}>
                <TableCell>{producto.Pr_Nombre}</TableCell>
                <TableCell>{producto.Un_Nombre}</TableCell>
                <TableCell align='center'>
                  <IconButton onClick={() => handleQuitar(producto.ClPr_Id)} title='Quitar producto'>
                    <Delete color='error' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {asignados.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align='center'>
                  Este cliente no tiene productos asignados -- verá el desplegable vacío al crear un Preservicio.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display='flex' justifyContent='flex-end' sx={{ marginTop: '1rem' }}>
        <Button onClick={onClose} variant='outlined' color='error'>
          Cerrar
        </Button>
      </Box>
    </div>
  );
});

export default AssignProductos;
